import { apiClient } from '@/lib/api-client';
import type {
  DomainVerifyResult,
  SitePageSeo,
  SlugAvailability,
  TenantDomain,
  TenantSite,
} from '@/types/site';

/**
 * Platform-admin management of a tenant's public website.
 *
 * Thin wrappers over `apiClient`, so the 401 → refresh → retry cycle and the
 * ApiError mapping (including the structured `details` a 409 carries) come for
 * free.
 */
export const siteService = {
  get(tenantId: string) {
    return apiClient<TenantSite>(`/tenants/${tenantId}/site`);
  },

  create(tenantId: string, payload: { slug: string; primary_location_id: string }) {
    return apiClient<TenantSite>(`/tenants/${tenantId}/site`, {
      method: 'POST',
      body: payload,
    });
  },

  update(
    tenantId: string,
    payload: Partial<{
      is_enabled: boolean;
      allow_indexing: boolean;
      hide_prices: boolean;
      show_stock_status: boolean;
      primary_location_id: string;
      notes: string;
    }>,
  ) {
    return apiClient<TenantSite>(`/tenants/${tenantId}/site`, {
      method: 'PATCH',
      body: payload,
    });
  },

  /** The old address stops working immediately and does not redirect. */
  setSlug(tenantId: string, slug: string) {
    return apiClient<TenantSite>(`/tenants/${tenantId}/site/slug`, {
      method: 'PUT',
      body: { slug },
    });
  },

  remove(tenantId: string) {
    return apiClient<{ removed: boolean }>(`/tenants/${tenantId}/site`, { method: 'DELETE' });
  },

  /** Advisory — the DB UNIQUE index is the authority, so submit can still 409. */
  checkSlug(slug: string) {
    return apiClient<SlugAvailability>(
      `/platform/sites/slug-available?slug=${encodeURIComponent(slug)}`,
    );
  },

  listPages(tenantId: string) {
    return apiClient<SitePageSeo[]>(`/tenants/${tenantId}/site/pages`);
  },

  upsertPage(
    tenantId: string,
    pageKey: string,
    payload: { meta_title?: string; meta_description?: string; noindex?: boolean },
  ) {
    return apiClient<SitePageSeo>(`/tenants/${tenantId}/site/pages/${pageKey}`, {
      method: 'PUT',
      body: payload,
    });
  },

  clearPage(tenantId: string, pageKey: string) {
    return apiClient<{ removed: boolean }>(`/tenants/${tenantId}/site/pages/${pageKey}`, {
      method: 'DELETE',
    });
  },
};

/**
 * Platform-admin management of a tenant's CUSTOM DOMAIN.
 *
 * Singular routes throughout — one domain per tenant, so this is a config
 * surface like `siteService`, not a collection like locations.
 */
export const domainService = {
  get(tenantId: string) {
    return apiClient<TenantDomain>(`/tenants/${tenantId}/domain`);
  },

  create(tenantId: string, payload: { hostname: string; notes?: string }) {
    return apiClient<TenantDomain>(`/tenants/${tenantId}/domain`, {
      method: 'POST',
      body: payload,
    });
  },

  /**
   * Changing `hostname` resets the domain to PENDING and frees the old name.
   * The response carries `teardown_required` and the exact command — the old
   * nginx vhost and certificate are NOT touched by this call.
   */
  update(
    tenantId: string,
    payload: Partial<{ hostname: string; notes: string; status: 'PENDING' | 'DISABLED' }>,
  ) {
    return apiClient<TenantDomain>(`/tenants/${tenantId}/domain`, {
      method: 'PATCH',
      body: payload,
    });
  },

  /** Always resolves — unpropagated DNS is an outcome, not an error. */
  verify(tenantId: string) {
    return apiClient<DomainVerifyResult>(`/tenants/${tenantId}/domain/verify`, { method: 'POST' });
  },

  rotateToken(tenantId: string) {
    return apiClient<TenantDomain>(`/tenants/${tenantId}/domain/rotate-token`, { method: 'POST' });
  },

  remove(tenantId: string) {
    return apiClient<{ removed: boolean; hostname: string }>(`/tenants/${tenantId}/domain`, {
      method: 'DELETE',
    });
  },
};
